import {splitUtcToLocalTime} from '@/helpers/dateUtils'
import {usePromotionConfig} from '@/hooks/usePromotionConfig'
import {usePromotionDetail} from '@/hooks/usePromotionDetail'
import {usePromotionOperations} from '@/hooks/usePromotionOperations'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {
  Condition,
  ConditionType,
  ItemType,
  Promotion,
  PromotionCondition,
  PromotionStatus,
  PromotionType,
} from '@/types/promotion'
import {PromotionSource} from '@/types/promotionTypes'
import {PromotionContionsTabList} from '@/utils/constants/promotionConstants'
import {mergePromotionConditions, parsePromotionDraft} from '@/utils/promotions/promotions'
import {createContext, ReactNode, SetStateAction, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useLocation, useParams} from 'react-router-dom'

interface PromotionContextType {
  promotion: Promotion
  promotionType: PromotionType
  promotionConfig: any
  updatePromotion: (oldPromomtion: SetStateAction<Promotion>) => void
  onResultChange: (updates: Partial<Promotion>) => void
  updateCondition: (updates: Partial<PromotionCondition>) => void
  handleUpdatePromotionConfig: (configData: Record<string, unknown>) => Promise<unknown>
  resetPromotion: () => void
  handleDraft: (options?: {isSavingForImageUpload?: boolean}) => Promise<unknown>
  savePromotion: () => Promise<unknown>
  handleUpdatePromotion: (options?: {isCreating?: boolean; isDraft?: boolean}) => Promise<unknown>
  maxDiscountModalVisible: boolean
  setMaxDiscountModalVisible: (visible: boolean) => void
  currentStep: number
  setCurrentStep: (step: number) => void
  isLoadingConfig: boolean
  cleanupBannerImage: () => Promise<void> // Add this new function
  allCustomers: boolean
  setPromoCondition: (index: number) => void
  activePromoCondition: number
}

const PromotionContext = createContext<PromotionContextType | undefined>(undefined)

const initialPromotion: Promotion = {
  name: '',
  onlineDiscountEligible: false,
  promotionType: PromotionType.BUY_SINGLE_PRODUCT,
  excludedItems: [],
  promotionConditions: [
    {
      sequence: 1,
    },
  ],

  segmentConfiguration: {
    includedSegments: [],
  },
  text: {
    // en: {
    //   productTag: 'Default Promotion Tag',
    //   bannerTag: 'Default Banner Tag',
    //   title: 'Default Promotion Title',
    // },
    // es_MX: {
    //   productTag: 'Default Promotion Tag',
    //   bannerTag: 'Default Banner Tag',
    //   title: 'Default Promotion Title',
    // },
  },
  bannerUrl: '',
  maxTimesPerOrder: 10,
  maxAllowedOrderPerStore: 1,
  maxAllowedPercentage: 50,
  maxFreeItemsPerOrder: 10,
  displayOrder: 1,
  displayOn: [],
  activeStartDate: '',
  activeEndDate: '',
  active: true,
  resetFrequencyInDays: 1,
  behavior: '',
  status: PromotionStatus.DRAFT,
  bannerType: '',
  landingPage: '',
  routeTypeId: 'Empty',
  source: PromotionSource.ADMIN_UI,
  allCustomers: true,
}

// @refresh skip
export const defaultConditon: Condition = {
  type: ConditionType.BUY,
  quantity: null,
  itemType: ItemType.ANY,
  items: [],
}

export const PromotionProvider = ({children, state}: {children: ReactNode; state?: any}) => {
  const location = useLocation()
  const params = useParams()
  const dispatch = useDispatch()

  // Merge location.state, URL params, and props.state, with props.state taking precedence
  // Use URL params as source of truth to preserve on page refresh
  const {
    promotionId,
    promotionType: statePromotionType,
    step,
    isDuplicating,
  } = {
    promotionId: params.promotionId,
    ...(location.state || {}),
    ...(state || {}),
  }

  const {promotionData} = usePromotionDetail(promotionId)
  const {promotionConfig, isLoadingConfig, handleUpdatePromotionConfig} = usePromotionConfig()
  const {handleDraft, savePromotion, handleUpdatePromotion} = usePromotionOperations()

  const promotionType = promotionData?.uiPromotionType ?? statePromotionType ?? initialPromotion.promotionType

  const createPromotionConditions = (type: PromotionType) => {
    switch (type) {
      case PromotionType.BUY_SINGLE_PRODUCT:
        return [
          {
            sequence: 1,
            conditionGroup: {
              conditions: [defaultConditon],
            },
          },
        ]
      case PromotionType.BUY_MULTIPLE_PRODUCTS:
        return [
          {
            sequence: 1,
            conditionGroup: {
              conditions: [defaultConditon, defaultConditon],
            },
          },
        ]
      default:
        return [
          {
            sequence: 1,
          },
        ]
    }
  }

  const [promotion, setPromotion] = useState<Promotion>(() => {
    if (promotionId) {
      return initialPromotion
    } else {
      return {
        ...initialPromotion,
        promotionType,
        promotionConditions: createPromotionConditions(promotionType),
      }
    }
  })

  const [maxDiscountModalVisible, setMaxDiscountModalVisible] = useState<boolean>(false)
  const [currentStep, setCurrentStep] = useState<number>(step ?? 1)
  const [activePromoCondition, setPromoCondition] = useState(0)
  useEffect(() => {
    if (promotionData && promotionId) {
      // Create a copy for editing (clear name and dates for duplication)
      const duplicatingOverrides = isDuplicating
        ? {name: '', activeStartDate: '', activeEndDate: '', activeStartTime: '', activeEndTime: ''}
        : {
            activeStartTime: splitUtcToLocalTime(promotionData?.activeStartDate),
            activeEndTime: splitUtcToLocalTime(promotionData?.activeEndDate),
          }

      const editablePromotion = {
        ...promotionData,
        ...duplicatingOverrides,
        source: PromotionSource.ADMIN_UI,
      }

      if (!promotionData.promotionConditions || promotionData.promotionConditions.length === 0) {
        editablePromotion.promotionConditions = createPromotionConditions(editablePromotion.promotionType)
      }

      setPromotion(editablePromotion)

      if (isDuplicating) {
        delete editablePromotion.id
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Promotion copied successfully.',
            type: 'success',
          })
        )
      }
    }
  }, [promotionData?.id])

  const onResultChange = useCallback((updates: Partial<Promotion>) => {
    setPromotion(prev => ({...prev, ...updates}))
  }, [])
  const updateCondition = useCallback((updates: Partial<PromotionCondition>) => {
    setPromotion(prev => ({
      ...prev,
      promotionConditions: mergePromotionConditions(prev.promotionConditions, updates),
    }))
  }, [])

  const resetPromotion = () => setPromotion(initialPromotion)

  const handleDraftWrapper = useCallback(
    async (options?: {isSavingForImageUpload?: boolean}) => {
      const defaultOptions = {isSavingForImageUpload: false}
      const mergedOptions = {...defaultOptions, ...options}

      if (promotion.id) {
        // Update existing promotion draft
        const updatedPromotion = await handleUpdatePromotion(promotion, {isDraft: true})
        setPromotion(updatedPromotion)
      } else {
        const savedPromotion = await handleDraft(parsePromotionDraft(promotion), mergedOptions)

        // When saving draft for image upload ensure we get back the ID and keep previous segment data intact
        if (mergedOptions.isSavingForImageUpload && savedPromotion) {
          setPromotion(prev => ({
            ...prev,
            id: savedPromotion.id,
            createdAt: savedPromotion.createdAt,
            country: savedPromotion.countryCode,
          }))
        }
      }
    },
    [promotion, handleUpdatePromotion, handleDraft]
  )

  // Add state to track original banner URL
  const [originalBannerUrl, setOriginalBannerUrl] = useState('')

  // Set original banner URL when promotion is loaded
  useEffect(() => {
    if (promotionData?.id) {
      const uom = promotionData?.promotionConditions?.[0]?.conditionGroup?.conditions?.[0]?.items[0].uom
      const uomIndex = PromotionContionsTabList.findIndex(item => item.uom === uom)
      setOriginalBannerUrl(promotionData.bannerUrl || '')
      setPromoCondition(uomIndex)
    }
  }, [promotionData?.id])

  // Add cleanup function
  const cleanupBannerImage = useCallback(async () => {
    if (!promotion?.id) return

    const newBannerUrl = originalBannerUrl || ''

    // Only update if the URL has changed
    if (promotion.bannerUrl !== newBannerUrl) {
      try {
        await handleUpdatePromotion(
          {
            ...promotion,
            bannerUrl: newBannerUrl,
          },
          {isDraft: true}
        )
      } catch (error) {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Failed to revert image changes',
            type: 'error',
          })
        )
      }
    }
  }, [originalBannerUrl, promotion, handleUpdatePromotion, dispatch])

  const value = useMemo(
    () => ({
      promotion,
      promotionConfig,
      onResultChange,
      resetPromotion,
      handleDraft: handleDraftWrapper,
      promotionType,
      updatePromotion: setPromotion,
      updateCondition,
      handleUpdatePromotionConfig,
      maxDiscountModalVisible,
      setMaxDiscountModalVisible,
      currentStep,
      setCurrentStep,
      isLoadingConfig,
      savePromotion: () => savePromotion(promotion),
      handleUpdatePromotion: ({isCreating = false, isDraft = false} = {}) =>
        handleUpdatePromotion(promotion, {isCreating, isDraft}),
      isDuplicating,
      cleanupBannerImage, // Add the cleanup function to context
      allCustomers: promotion.allCustomers,
      setPromoCondition,
      activePromoCondition,
    }),
    [
      onResultChange,
      promotion,
      promotionConfig,
      promotionType,
      updateCondition,
      handleUpdatePromotionConfig,
      maxDiscountModalVisible,
      handleDraftWrapper,
      currentStep,
      setCurrentStep,
      savePromotion,
      isLoadingConfig,
      isDuplicating,
      handleUpdatePromotion,
      cleanupBannerImage,
      setPromoCondition,
      activePromoCondition,
    ]
  )

  return <PromotionContext.Provider value={value}>{children}</PromotionContext.Provider>
}

export const usePromotion = () => {
  const context = useContext(PromotionContext)
  if (!context) throw new Error('usePromotion must be used within a PromotionProvider')
  return context
}
